import { useMemo } from "react";

import FilterPanel from "components/partials/filter-panel/filter-panel";
import ColumnOptions from "components/partials/column-options/column-options";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import {
  useGetCampaignClassTypesQuery,
  useGetPopulationTypesQuery,
  useGetTouchpointContentTypesQuery,
} from "hooks/api/dictionary/dictionary";

import { TouchpointType } from "types/touchpoint";
import { FiltersToApplyOnCreativeTable } from "containers/admin/clients/campaign/components/filters-on-creative-table/filters-on-creative-table.types";
import { UserTableLayout } from "hooks/api/use-user-layout";
import { ColumnOption } from "types/table";

import { CREATIVE_TABLE_FILTERS_DATA } from "containers/admin/clients/campaign/components/filters-on-creative-table/filters-on-creative-table.consts";

import { Filter, FilterOption, FiltersStateOnTable } from "types/filters";

interface FiltersOnCreativeTableProps extends FiltersStateOnTable<FiltersToApplyOnCreativeTable> {
  tableColumnsConfig: UserTableLayout;
  columnOptions: ColumnOption[];
  onToggleColumn: (columnId: string) => void;
  isFetching?: boolean;
}

const FiltersOnCreativeTable = ({
  filtersToApply,
  setFiltersToApply,
  tableColumnsConfig,
  columnOptions,
  onToggleColumn,
  isFetching = false,
}: FiltersOnCreativeTableProps) => {
  const selectedCampaign = useCurrentCampaign();

  const { data: contentTypes = [] } = useGetTouchpointContentTypesQuery(
    selectedCampaign.campaignObjective,
  );
  const { data: classTypes = [] } = useGetCampaignClassTypesQuery(selectedCampaign.campaignObjective);
  const { data: populationTypes = [] } = useGetPopulationTypesQuery();

  const filtersToDisplay = useMemo(() => {
    return CREATIVE_TABLE_FILTERS_DATA.map((filter) => {
      if (filter.filterId === "contentType") {
        return {
          ...filter,
          options: contentTypes.map((contentType) => ({
            name: contentType.name as TouchpointType,
            description: contentType.description,
          })),
        };
      }
      if (filter.filterId === "classType") {
        return {
          ...filter,
          options: classTypes.map((classType) => ({
            name: classType.name,
            description: classType.description,
          })),
        };
      }
      if (filter.filterId === "populationType") {
        return {
          ...filter,
          options: populationTypes.map((populationType) => ({
            name: populationType.name,
            description: populationType.description,
          })),
        };
      }
      return filter;
    });
  }, [contentTypes, classTypes, populationTypes]);

  const handleFilterChange = (filter: Filter, option: FilterOption) => {
    const filterKey = filter.filterId as keyof FiltersToApplyOnCreativeTable;
    const currentValues = (filtersToApply[filterKey] as string[]) || [];
    const isSelected = currentValues.includes(option.name);

    setFiltersToApply({
      ...filtersToApply,
      [filterKey]: isSelected
        ? currentValues.filter((item) => item !== option.name)
        : [...currentValues, option.name],
    });
  };

  return (
    <>
      <FilterPanel
        filters={filtersToDisplay}
        currentFilters={filtersToApply}
        onFilterChange={handleFilterChange}
        isFetching={isFetching}
      />

      <ColumnOptions
        tableConfigKey="campaignCreatives"
        tableColumnsConfig={tableColumnsConfig}
        columnOptions={columnOptions}
        onToggleColumn={onToggleColumn}
      />
    </>
  );
};

export default FiltersOnCreativeTable;
